import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = 'https://starnivesh.onrender.com';
//const BACKEND_URL = 'https://localhost:3000';

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      fetchBlogs();
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs/search/${encodeURIComponent(query)}`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error searching blogs:', error);
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-text-secondary text-xl" data-testid="blogs-loading">Loading blogs...</p>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      <section className="py-20" style={{backgroundColor: '#1F2937'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-16">
            <div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4" data-testid="blogs-title">
                Investment <span style={{color: "#F5A623"}}>Insights</span>
              </h1>
              <p className="text-xl text-text-secondary max-w-2xl">
                Learn about mutual funds, goal-based investing, and building long-term wealth through our expert articles.
              </p>
            </div>
            
            {/* Search Bar - Top Right */}
            <div className="w-full md:w-96 relative flex-shrink-0">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-muted h-5 w-5" />
              <Input
                type="text"
                placeholder="Search blogs..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                //className="pl-12 py-3 text-base bg-background-paper border-white/10 text-text-primary placeholder:text-text-muted" --change colour coding
                className="pl-12 py-3 text-base bg-gray-800 border-gray-50 text-text-primary placeholder:text-text-muted"
                data-testid="blogs-search-input"
              />
              {searching && <p className="text-text-muted text-sm mt-1">Searching...</p>}
              {searchQuery && !searching && (
                <p className="text-text-secondary text-sm mt-1">
                  {blogs.length} result{blogs.length !== 1 ? 's' : ''} found
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.map((blog, index) => (
              <Link key={blog.id} to={`/blogs/${blog.slug}`} data-testid={`blog-card-${index}`}>
                <div className="glass-effect rounded-2xl overflow-hidden hover:-translate-y-1 transition-all group h-full flex flex-col">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center space-x-4 text-sm text-text-muted mb-3">
                      <span className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {blog.author}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="font-heading text-xl font-bold text-text-primary mb-3 group-hover:text-primary transition-colors flex-1">
                      {blog.title}
                    </h3>
                    <p className="text-text-secondary mb-4 line-clamp-2">
                      {blog.excerpt}
                    </p>
                    <span className="text-primary font-semibold flex items-center">
                      Read More <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
